/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback } from 'react';

import { css, jsx } from '@compiled/react';

import Button from '@atlaskit/button/custom-theme-button';
import AddIcon from '@atlaskit/icon/core/add';
import CrossIcon from '@atlaskit/icon/core/cross';
import {
	TopNavButton,
	TopNavIconButton,
} from '@atlaskit/navigation-system/experimental/top-nav-button';
import { SpotlightPulse, SpotlightTarget } from '@atlaskit/onboarding';
import { fg } from '@atlaskit/platform-feature-flags';
import { type TriggerProps } from '@atlaskit/popup';
import { Hide, Show, UNSAFE_useMediaQuery as useMediaQuery } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { TRIGGER_BUTTON_TEST_ID } from '../../../constants';
import { useMenuControl } from '../../../context';
import { useUniversalCreateAnalytics } from '../../../hooks';
import type { UniversalCreateButtonProps } from '../../../types';

const triggerButton = css({
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		// below 768px https://staging.atlassian.design/components/primitives/responsive/breakpoints/examples#media-queries
		// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles
		'@media not all and (min-width: 48rem)': {
			display: 'block',

			// to match with the icon button spacing
			paddingLeft: token('space.100', '8px'),
			paddingRight: token('space.100', '8px'),
			// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
			'> span > span': {
				// enforce icon has no extra padding
				paddingLeft: 0,
				paddingRight: 0,
			},
		},
	},
});

/**
 * Refactor this whole logic after T-25, we should use the proper Create component instead.
 * https://jplat.atlassian.net/browse/BLU-7401
 */
const nav3ButtonTextStyles = css({
	// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
		'@media not all and (min-width: 48rem)': {
			// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
			'&:hover': {
				paddingLeft: token('space.100', '8px'),
				paddingRight: token('space.100', '8px'),
			},
			// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
			'span:nth-of-type(1)': {
				marginLeft: 0,
				marginRight: 0,
			},
			// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
			'span:nth-of-type(2)': {
				display: 'none',
			},
		},
	},
});

export type TriggerButtonProps = {
	buttonTheme?: UniversalCreateButtonProps['buttonTheme'];
	children: string;
	enableTriggerPulse?: boolean;
	isNav4Enabled?: boolean;
	isOpen: boolean;
	onClick: () => void;
	onMouseEnter?: UniversalCreateButtonProps['onMouseEnter'];
	onMouseLeave?: UniversalCreateButtonProps['onMouseLeave'];
	testId?: string;
	triggerButtonSpotlightTarget?: string;
	triggerProps: TriggerProps;
};
export function TriggerButton({
	triggerProps,
	testId,
	onClick,
	children,
	isOpen,
	triggerButtonSpotlightTarget,
	enableTriggerPulse,
	isNav4Enabled = true,
	buttonTheme,
	onMouseEnter,
	onMouseLeave,
}: TriggerButtonProps) {
	const { product } = useMenuControl();
	const { fireTriggerButtonClicked, fireCreateButtonClicked } = useUniversalCreateAnalytics();
	const responsiveButtonUpdateEnabled = fg('navx-uc-responsive-button-update');

	const onButtonClick = useCallback(() => {
		if (fg('navx-uc-unified-instrumentation')) {
			fireCreateButtonClicked({ itemState: isOpen ? 'collapsed' : 'expanded' });
		} else {
			fireTriggerButtonClicked();
		}
		onClick();
	}, [fireTriggerButtonClicked, fireCreateButtonClicked, isOpen, onClick]);

	const CreateIcon = isOpen ? CrossIcon : AddIcon;

	const enterEvent = useCallback(
		(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			onMouseEnter?.(event);
		},
		[onMouseEnter],
	);

	const leaveEvent = useCallback(
		(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			onMouseLeave?.(event);
		},
		[onMouseLeave],
	);

	const isDesktop = useMediaQuery('above.sm')?.matches ?? false;

	const button = (
		<div css={triggerButton}>
			{product === 'confluence' && !isNav4Enabled ? (
				<div css={nav3ButtonTextStyles}>
					<Button
						{...triggerProps}
						appearance="primary"
						theme={buttonTheme}
						onMouseEnter={enterEvent}
						onMouseLeave={leaveEvent}
						onClick={onButtonClick}
						iconBefore={<CreateIcon label="" data-vc="global-content-create-button" />}
						testId={testId || TRIGGER_BUTTON_TEST_ID}
						aria-label={children}
						isSelected={isOpen}
					>
						<Show above="sm">{children}</Show>
					</Button>
				</div>
			) : (
				<Tooltip
					content={children}
					isScreenReaderAnnouncementDisabled
					position="bottom"
					ignoreTooltipPointerEvents={fg('gc_button_tooltip_ignore_pointer_events')}
				>
					{/* disabling the tooltip content being announced as it's the same as the children content */}
					<TopNavButton
						{...triggerProps}
						appearance="primary"
						onClick={onButtonClick}
						onMouseEnter={enterEvent}
						onMouseLeave={leaveEvent}
						isSelected={isOpen}
						iconBefore={(iconProps) => <CreateIcon {...iconProps} label="" />}
						testId={testId || TRIGGER_BUTTON_TEST_ID}
						aria-label={children}
					>
						<Show above="sm">{children}</Show>
					</TopNavButton>
				</Tooltip>
			)}
		</div>
	);

	const responsiveSafeButton = (
		<Tooltip content={children} isScreenReaderAnnouncementDisabled position="bottom">
			<Show above="sm">
				<TopNavButton
					{...(isDesktop ? triggerProps : {})}
					appearance="primary"
					onClick={onButtonClick}
					onMouseEnter={enterEvent}
					onMouseLeave={leaveEvent}
					isSelected={isOpen}
					iconBefore={(iconProps) => <CreateIcon {...iconProps} label="" />}
					testId={testId || TRIGGER_BUTTON_TEST_ID}
					data-responsive-variant="desktop"
					aria-label={children}
				>
					{children}
				</TopNavButton>
			</Show>
			<Hide above="sm">
				<TopNavIconButton
					{...(!isDesktop ? triggerProps : {})}
					appearance="primary"
					onClick={onButtonClick}
					onMouseEnter={enterEvent}
					onMouseLeave={leaveEvent}
					isSelected={isOpen}
					icon={CreateIcon}
					testId={testId || TRIGGER_BUTTON_TEST_ID}
					data-responsive-variant="mobile"
					label={children}
				/>
			</Hide>
		</Tooltip>
	);

	const buttonToRender =
		responsiveButtonUpdateEnabled && isNav4Enabled ? responsiveSafeButton : button;

	return triggerButtonSpotlightTarget ? (
		<SpotlightTarget name={triggerButtonSpotlightTarget}>
			<SpotlightPulse radius={3} pulse={enableTriggerPulse}>
				{buttonToRender}
			</SpotlightPulse>
		</SpotlightTarget>
	) : (
		buttonToRender
	);
}
